import { useEffect, useCallback, useRef } from 'react';
import {
  VStack,
  HStack,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Box,
  Tooltip,
} from '@chakra-ui/react';
import {
  AdvancedAccordion,
  useCalibrationStatus,
  useSheetContext,
} from './shared';
import { AlgorithmSettingsProps } from 'src/components/auto-shapes/types';
import {
  AutoShapeAlgorithmType,
  RoomDetection2DOptions,
} from 'src/gql/graphql';

// Default values for CALIBRATED pages (physical units)
const DEFAULTS_CALIBRATED = {
  wallThickness: 80, // mm
  maxGap: 1000, // mm
  minRoomArea: 1, // m²
  simplifyTolerance: 75, // mm
};

// Default values for NON-CALIBRATED pages (pixel units)
const DEFAULTS_UNCALIBRATED = {
  wallThickness: 2, // px
  maxGap: 90, // px
  minRoomArea: 500, // px²
  simplifyTolerance: 3, // px
};

// Slider configurations for calibrated pages (physical units)
const SLIDER_CONFIGS_CALIBRATED = {
  wallThickness: { min: 50, max: 500, step: 10, unit: 'mm' },
  maxGap: { min: 200, max: 2000, step: 50, unit: 'mm' },
  minRoomArea: { min: 0.5, max: 10, step: 0.5, unit: 'm²' },
  simplifyTolerance: { min: 10, max: 200, step: 5, unit: 'mm' },
};

// Slider configurations for non-calibrated pages (pixel units)
const SLIDER_CONFIGS_UNCALIBRATED = {
  wallThickness: { min: 1, max: 10, step: 1, unit: 'px' },
  maxGap: { min: 20, max: 200, step: 5, unit: 'px' },
  minRoomArea: {
    min: 100,
    max: 5000,
    step: 100,
    unit: 'px²',
  },
  simplifyTolerance: { min: 1, max: 20, step: 1, unit: 'px' },
};

function SliderWithLabel({
  label,
  value,
  min,
  max,
  step,
  onChange,
  displayValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  displayValue?: string;
}) {
  return (
    <VStack align="stretch" spacing={1}>
      <HStack justify="space-between">
        <Text color="gray.500" fontSize="xs">
          {label}
        </Text>
        <Text fontSize="xs" color="gray.600" fontFamily="mono">
          {displayValue ?? value}
        </Text>
      </HStack>
      <Slider
        colorScheme="purple"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
      >
        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb boxSize={4} />
      </Slider>
    </VStack>
  );
}

type ParameterSliderProps = {
  label: string;
  tooltip: string;
  value: number;
  defaultValue: number;
  config: { min: number; max: number; step: number; unit: string };
  onChange: (v: number) => void;
};

function ParameterSlider({
  label,
  tooltip,
  value,
  defaultValue,
  config,
  onChange,
}: ParameterSliderProps) {
  return (
    <Tooltip label={tooltip} hasArrow>
      <Box>
        <SliderWithLabel
          label={label}
          value={value ?? defaultValue}
          min={config.min}
          max={config.max}
          step={config.step}
          onChange={onChange}
          displayValue={`${value ?? defaultValue}${config.unit}`}
        />
      </Box>
    </Tooltip>
  );
}

type MinRoomAreaSliderProps = {
  value: number;
  defaultValue: number;
  config: {
    min: number;
    max: number;
    step: number;
    unit: string;
  };
  onChange: (v: number) => void;
  isCalibrated: boolean;
};

function MinRoomAreaSlider({
  value,
  defaultValue,
  config,
  onChange,
  isCalibrated,
}: MinRoomAreaSliderProps) {
  const tooltip = isCalibrated
    ? 'Minimum area for a region to be detected as a room (m²)'
    : 'Minimum area for a region to be detected as a room (px²)';

  return (
    <Tooltip label={tooltip} hasArrow>
      <Box>
        <SliderWithLabel
          label="Min Room Area"
          value={value ?? defaultValue}
          min={config.min}
          max={config.max}
          step={config.step}
          onChange={onChange}
          displayValue={`${value ?? defaultValue}${config.unit}`}
        />
      </Box>
    </Tooltip>
  );
}

function useSyncSheetOptions(
  currentSheetId: string | undefined,
  currentPageNumber: number,
  options: RoomDetection2DOptions,
  isCalibrated: boolean,
  onChange: (opts: RoomDetection2DOptions) => void
) {
  // Track whether we've initialized for this sheet/calibration combination
  const initializedRef = useRef<string | null>(null);
  const calibrationKey = `${currentSheetId}-${currentPageNumber}-${isCalibrated}`;

  useEffect(() => {
    if (!currentSheetId) return;

    // Update if sheet changed OR if this is first time seeing this calibration state
    const needsUpdate =
      currentSheetId !== options.sheetId ||
      initializedRef.current !== calibrationKey;

    if (needsUpdate) {
      const newDefaults = isCalibrated
        ? DEFAULTS_CALIBRATED
        : DEFAULTS_UNCALIBRATED;
      onChange({
        sheetId: currentSheetId,
        pageNumber: currentPageNumber,
        wallThickness: newDefaults.wallThickness,
        maxGap: newDefaults.maxGap,
        minRoomArea: newDefaults.minRoomArea,
        simplifyTolerance: newDefaults.simplifyTolerance,
      });
      initializedRef.current = calibrationKey;
    }
  }, [
    currentSheetId,
    currentPageNumber,
    options.sheetId,
    isCalibrated,
    calibrationKey,
    onChange,
  ]);
}

function useValiditySync(
  options: RoomDetection2DOptions,
  onValidityChange: (valid: boolean) => void
) {
  useEffect(() => {
    const isValid =
      Boolean(options.sheetId) && Number.isFinite(options.pageNumber);
    onValidityChange(isValid);
  }, [options.sheetId, options.pageNumber, onValidityChange]);
}

type ParameterControlsProps = {
  options: RoomDetection2DOptions;
  defaults: typeof DEFAULTS_CALIBRATED;
  configs: typeof SLIDER_CONFIGS_CALIBRATED;
  isCalibrated: boolean;
  updateOption: (key: keyof RoomDetection2DOptions, val: number) => void;
};

function ParameterControls({
  options,
  defaults,
  configs,
  isCalibrated,
  updateOption,
}: ParameterControlsProps) {
  const lengthUnit = isCalibrated ? 'millimeters' : 'pixels';

  return (
    <AdvancedAccordion>
      <ParameterSlider
        label="Wall Thickness"
        tooltip={`Wall thickness between rooms in ${lengthUnit}`}
        value={options.wallThickness ?? defaults.wallThickness}
        defaultValue={defaults.wallThickness}
        config={configs.wallThickness}
        onChange={(v) => updateOption('wallThickness', v)}
      />

      <ParameterSlider
        label="Max Gap"
        tooltip={`Maximum gap size to close (${configs.maxGap.unit})`}
        value={options.maxGap ?? defaults.maxGap}
        defaultValue={defaults.maxGap}
        config={configs.maxGap}
        onChange={(v) => updateOption('maxGap', v)}
      />

      <MinRoomAreaSlider
        value={options.minRoomArea ?? defaults.minRoomArea}
        defaultValue={defaults.minRoomArea}
        config={configs.minRoomArea}
        onChange={(v) => updateOption('minRoomArea', v)}
        isCalibrated={isCalibrated}
      />

      <ParameterSlider
        label="Simplification"
        tooltip={`Polygon simplification (${configs.simplifyTolerance.unit})`}
        value={options.simplifyTolerance ?? defaults.simplifyTolerance}
        defaultValue={defaults.simplifyTolerance}
        config={configs.simplifyTolerance}
        onChange={(v) => updateOption('simplifyTolerance', v)}
      />
    </AdvancedAccordion>
  );
}

export function RoomDetection2DSettings({
  value,
  onChange,
  onValidityChange,
}: AlgorithmSettingsProps<AutoShapeAlgorithmType.RoomDetection_2D>) {
  const { currentSheetId, currentPageNumber, sheetName } = useSheetContext();
  const isCalibrated = useCalibrationStatus(currentSheetId, currentPageNumber);

  const defaults = isCalibrated ? DEFAULTS_CALIBRATED : DEFAULTS_UNCALIBRATED;
  const configs = isCalibrated
    ? SLIDER_CONFIGS_CALIBRATED
    : SLIDER_CONFIGS_UNCALIBRATED;

  useSyncSheetOptions(
    currentSheetId,
    currentPageNumber,
    value,
    isCalibrated,
    onChange
  );
  useValiditySync(value, onValidityChange);

  const updateOption = useCallback(
    (key: keyof RoomDetection2DOptions, val: number) => {
      onChange({ ...value, [key]: val });
    },
    [value, onChange]
  );

  return (
    <VStack spacing={3} align="stretch" width="100%">
      <ParameterControls
        options={value}
        defaults={defaults}
        configs={configs}
        isCalibrated={isCalibrated}
        updateOption={updateOption}
      />
    </VStack>
  );
}
